/**
 * Normalisation des numéros tunisiens au format `+216XXXXXXXX`
 * (docs/INGESTION_EMAIL.md § Personne et doublons). Le numéro normalisé est la
 * première clé de rapprochement des personnes : une erreur ici crée des
 * doublons ou, pire, fusionne deux candidats.
 */
const INDICATIF = '216';

/** Premier chiffre des numéros tunisiens attribués (mobiles et fixes). */
const PREMIERS_CHIFFRES = new Set(['2', '3', '4', '5', '7', '9']);

/** Les chiffres arabo-indiens apparaissent dans certains CV et signatures. */
function occidentaliser(brut: string): string {
  return brut.replace(/[٠-٩۰-۹]/g, (c) => {
    const code = c.charCodeAt(0);
    const base = code >= 0x06f0 ? 0x06f0 : 0x0660;
    return String(code - base);
  });
}

export function normaliserTelephone(brut: string | null | undefined): string | null {
  if (!brut) return null;

  const occidentalise = occidentaliser(brut);

  let chiffres = occidentalise.replace(/[^\d+]/g, '');
  if (chiffres.startsWith('+')) chiffres = chiffres.slice(1);
  chiffres = chiffres.replace(/\D/g, '');

  if (chiffres.startsWith('00')) chiffres = chiffres.slice(2);
  if (chiffres.startsWith(INDICATIF) && chiffres.length === INDICATIF.length + 8) {
    chiffres = chiffres.slice(INDICATIF.length);
  }
  // Un 0 en tête est une habitude française appliquée à tort au numéro local.
  if (chiffres.length === 9 && chiffres.startsWith('0')) chiffres = chiffres.slice(1);

  if (chiffres.length !== 8) return null;
  if (!PREMIERS_CHIFFRES.has(chiffres[0]!)) return null;
  return `+${INDICATIF}${chiffres}`;
}

/** Longueur d'un numéro international plausible, indicatif compris (UIT E.164). */
const CHIFFRES_MIN = 8;
const CHIFFRES_MAX = 15;

/**
 * Numéro sur lequel on peut **joindre** quelqu'un : un numéro tunisien est
 * ramené à la forme canonique `+216XXXXXXXX`, un numéro **étranger** (préfixé
 * `+` ou `00`) est seulement nettoyé — on ne connaît pas les plans de
 * numérotation étrangers, et inventer une règle ferait plus de dégâts que de
 * garder la saisie (arbitrage du 2026-09-20).
 *
 * `normaliserTelephone` reste la seule normalisation du numéro **principal**,
 * clé de rapprochement des personnes : ce qui sort d'ici ne s'y substitue
 * jamais, et ne rapproche donc jamais deux fiches.
 */
export function normaliserNumeroJoignable(brut: string | null | undefined): string | null {
  if (!brut) return null;
  const propre = occidentaliser(brut).trim();
  const international = /^(\+|00)/.test(propre.replace(/[\s.\-()]/g, ''));

  const tunisien = normaliserTelephone(propre);
  if (tunisien) return tunisien;
  if (!international) return null;

  let chiffres = propre.replace(/[^\d+]/g, '');
  if (chiffres.startsWith('+')) chiffres = chiffres.slice(1);
  chiffres = chiffres.replace(/\D/g, '');
  if (chiffres.startsWith('00')) chiffres = chiffres.slice(2);
  if (chiffres.length < CHIFFRES_MIN || chiffres.length > CHIFFRES_MAX) return null;
  return `+${chiffres}`;
}

/** `+21698765432` → `98 765 432` ; toute autre valeur est rendue telle quelle. */
export function telephoneLisible(normalise: string): string {
  const national = /^\+216(\d{8})$/.exec(normalise)?.[1];
  return national ? `${national.slice(0, 2)} ${national.slice(2, 5)} ${national.slice(5)}` : normalise;
}

/**
 * Repère les numéros dans un corps de message ou un CV. Renvoie les numéros
 * normalisés, sans doublon, dans l'ordre d'apparition.
 */
export function extraireTelephones(texte: string): string[] {
  const candidats = texte.match(/(?:\+|00)?[\d٠-٩۰-۹][\d\s.()‐-―-]{6,20}/g);
  if (!candidats) return [];
  const trouves: string[] = [];
  for (const candidat of candidats) {
    const normalise = normaliserTelephone(candidat);
    if (normalise && !trouves.includes(normalise)) trouves.push(normalise);
  }
  return trouves;
}

const MOTIF_EMAIL = /[\w.+-]+@[\w-]+(?:\.[\w-]+)*\.[A-Za-z]{2,18}/g;

/**
 * Domaines de premier niveau rencontrés dans les candidatures. Ils servent à
 * recouper une adresse collée au mot suivant : certaines notifications
 * produisent « ...@gmail.comMerci pour votre confiance », où l'expression
 * régulière avalerait « commerci » comme extension.
 */
const EXTENSIONS = [
  'com', 'net', 'org', 'info', 'biz', 'edu', 'gov', 'int',
  'tn', 'fr', 'de', 'it', 'es', 'uk', 'be', 'ch', 'ca', 'ma', 'dz', 'ly', 'eu',
  'io', 'co', 'me', 'tv', 'app', 'dev', 'xyz', 'online', 'site', 'cloud',
];

const EXTENSIONS_PAR_LONGUEUR = [...EXTENSIONS].sort((a, b) => b.length - a.length);

/**
 * Coupe une adresse dont l'extension a absorbé le mot suivant. Sans cela,
 * « a@b.comMerci » deviendrait une adresse en « .commerci » et le candidat
 * serait injoignable.
 *
 * Une extension qui est elle-même connue n'est jamais touchée — c'est ce qui
 * distingue « .com » (valide) de « .commerci » (recollé), et évite de tronquer
 * « .online » en « .on ».
 */
export function couperApresExtension(adresse: string): string {
  const dernierPoint = adresse.lastIndexOf('.');
  if (dernierPoint < 0) return adresse;

  const queue = adresse.slice(dernierPoint + 1).toLowerCase();
  if (EXTENSIONS.includes(queue)) return adresse;

  // Extension inconnue : on garde le plus long préfixe qui en soit une.
  const prefixe = EXTENSIONS_PAR_LONGUEUR.find((ext) => queue.startsWith(ext));
  return prefixe ? adresse.slice(0, dernierPoint + 1 + prefixe.length) : adresse;
}

export function normaliserEmail(brut: string | null | undefined): string | null {
  if (!brut) return null;
  const trouve = brut.match(MOTIF_EMAIL)?.[0];
  return trouve ? couperApresExtension(trouve).toLowerCase() : null;
}

export function extraireEmails(texte: string): string[] {
  return emailsAvecPosition(texte).map((e) => e.email);
}

/** Adresses d'un texte, sans doublon, avec la position de leur première apparition. */
export function emailsAvecPosition(texte: string): Array<{ email: string; position: number }> {
  const premieres = new Map<string, number>();
  for (const trouve of texte.matchAll(MOTIF_EMAIL)) {
    const email = couperApresExtension(trouve[0]).toLowerCase();
    if (!premieres.has(email)) premieres.set(email, trouve.index ?? 0);
  }
  return [...premieres].map(([email, position]) => ({ email, position }));
}
